<template>
  <section class="int-section int-section--api">
    <div class="int-section__accent" />
    <div class="int-section__inner">
      <header class="int-section__head">
        <div class="int-section__head-left">
          <div class="int-section__icon" aria-hidden="true">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <div>
            <h2 class="int-section__title">{{ t('integracao.leadApi.sectionTitle') }}</h2>
            <p class="int-section__desc">{{ t('integracao.leadApi.sectionDesc') }}</p>
          </div>
        </div>
        <div class="int-section__head-actions">
          <button type="button" class="int-section__cta int-section__cta--ghost" @click="explorerOpen = true">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            {{ t('integracao.leadApi.explorerOpen') }}
          </button>
          <button type="button" class="int-section__cta" @click="openCreateKey">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            {{ t('integracao.leadApi.newKey') }}
          </button>
        </div>
      </header>

      <div v-if="loading" class="int-section__loading">
        <span class="int-section__spinner" aria-hidden="true" />
        {{ t('common.loading') }}
      </div>

      <div v-else-if="!keys.length" class="int-section__empty">
        <div class="int-section__empty-icon" aria-hidden="true">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
          </svg>
        </div>
        <p>{{ t('integracao.leadApi.empty') }}</p>
      </div>

      <div v-else class="int-item-list">
        <article v-for="k in keys" :key="k.id" class="int-item-card">
          <div class="int-item-card__main">
            <h3 class="int-item-card__title">{{ k.nome }}</h3>
            <div class="int-item-card__meta">
              <span :class="['int-badge', k.ativo ? 'int-badge--on' : 'int-badge--off']">
                <span class="int-badge__dot" aria-hidden="true" />
                {{ k.ativo ? t('integracao.leadApi.active') : t('integracao.leadApi.revoked') }}
              </span>
              <code class="int-item-card__code">{{ k.keyPrefix }}</code>
              <span>{{ t('integracao.leadApi.lastUsed') }}: {{ formatDate(k.lastUsedAt) }}</span>
            </div>
          </div>
          <div class="int-item-card__actions">
            <button type="button" class="int-action-btn int-action-btn--primary" @click="rotateKey(k)">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {{ t('integracao.leadApi.rotate') }}
            </button>
            <button
              v-if="k.ativo"
              type="button"
              class="int-action-btn int-action-btn--danger"
              @click="revokeKey(k)"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              {{ t('integracao.leadApi.revoke') }}
            </button>
          </div>
        </article>
      </div>

      <div class="int-api-panel">
        <div class="int-api-panel__head">
          <h3 class="int-api-panel__title">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            {{ t('integracao.leadApi.howToTitle') }}
          </h3>
        </div>
        <div class="int-api-endpoint">
          <span class="int-api-endpoint__method">POST</span>
          <span class="int-api-endpoint__url">{{ apiUrl }}</span>
        </div>
        <p class="int-api-panel__hint">{{ t('integracao.leadApi.headerHint') }}</p>
        <div class="int-api-tabs">
          <button
            v-for="tab in tabs"
            :key="tab"
            type="button"
            :class="['int-api-tab', { 'int-api-tab--active': activeTab === tab }]"
            @click="activeTab = tab"
          >
            {{ tab }}
          </button>
        </div>
        <div class="int-api-code-wrap">
          <pre class="int-api-code"><code>{{ codeSample }}</code></pre>
        </div>
        <button type="button" class="int-api-copy" @click="copyCode">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          {{ t('integracao.leadApi.copyCode') }}
        </button>
      </div>

      <div class="int-api-panel int-api-panel--extra">
        <div class="int-api-panel__head">
          <h3 class="int-api-panel__title">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h7" />
            </svg>
            {{ t('integracao.leadApi.endpointsTitle') }}
          </h3>
        </div>
        <p class="int-api-panel__hint">{{ t('integracao.leadApi.endpointsDesc') }}</p>

        <article
          v-for="ep in crmApiEndpoints"
          :key="ep.id"
          class="int-api-endpoint-card"
        >
          <div class="int-api-endpoint">
            <span :class="['int-api-endpoint__method', `int-api-endpoint__method--${ep.method.toLowerCase()}`]">{{ ep.method }}</span>
            <span class="int-api-endpoint__url">{{ ep.url }}</span>
          </div>
          <h4 class="int-api-endpoint-card__title">{{ t(ep.titleKey) }}</h4>
          <p class="int-api-endpoint-card__desc">{{ t(ep.descKey) }}</p>
          <div class="int-api-code-wrap">
            <pre class="int-api-code"><code>{{ ep.curl }}</code></pre>
          </div>
          <button type="button" class="int-api-copy int-api-copy--inline" @click="copyText(ep.curl)">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            {{ t('integracao.leadApi.copyCode') }}
          </button>
        </article>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="createModal" class="int-modal-overlay" @click.self="createModal = false">
        <div class="int-modal int-modal--sm" role="dialog" aria-modal="true" @click.stop>
          <div class="int-modal__accent" />
          <header class="int-modal__header">
            <div class="int-modal__icon-wrap">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <div class="int-modal__titles">
              <h3>{{ t('integracao.leadApi.newKey') }}</h3>
              <p class="int-modal__subtitle">{{ t('integracao.leadApi.createKeySubtitle') }}</p>
            </div>
            <button type="button" class="int-modal__close" :aria-label="t('common.close')" @click="createModal = false">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>
          <div class="int-modal__body">
            <div class="int-modal__field">
              <label for="api-key-name">{{ t('integracao.leadApi.keyName') }}</label>
              <input
                id="api-key-name"
                v-model="newKeyName"
                type="text"
                :placeholder="t('integracao.leadApi.keyNamePlaceholder')"
                @keyup.enter="createKey"
              />
            </div>
          </div>
          <footer class="int-modal__footer">
            <button type="button" class="int-btn int-btn--ghost" @click="createModal = false">
              {{ t('common.cancel') }}
            </button>
            <button type="button" class="int-btn int-btn--primary" :disabled="creating" @click="createKey">
              {{ creating ? t('common.loading') : t('common.create') }}
            </button>
          </footer>
        </div>
      </div>

      <div v-if="secretModal" class="int-modal-overlay" @click.self="secretModal = false">
        <div class="int-modal int-modal--md" role="dialog" aria-modal="true" @click.stop>
          <div class="int-modal__accent int-modal__accent--emerald" />
          <header class="int-modal__header">
            <div class="int-modal__icon-wrap int-modal__icon-wrap--emerald">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div class="int-modal__titles">
              <h3>{{ t('integracao.leadApi.secretTitle') }}</h3>
              <p class="int-modal__subtitle">{{ t('integracao.leadApi.secretWarning') }}</p>
            </div>
            <button type="button" class="int-modal__close" :aria-label="t('common.close')" @click="secretModal = false">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>
          <div class="int-modal__body">
            <div class="int-modal__alert int-modal__alert--warn">
              {{ t('integracao.leadApi.secretOnceHint') }}
            </div>
            <div class="int-modal__secret-box">{{ revealedSecret }}</div>
          </div>
          <footer class="int-modal__footer">
            <button type="button" class="int-btn int-btn--ghost" @click="secretModal = false">
              {{ t('common.close') }}
            </button>
            <button type="button" class="int-btn int-btn--success" @click="copySecret">
              {{ t('integracao.leadApi.copySecret') }}
            </button>
          </footer>
        </div>
      </div>
    </Teleport>

    <LeadApiExplorerModal v-model="explorerOpen" :initial-api-key="revealedSecret" />
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import api from '@/services/api'
import LeadApiExplorerModal from '@/components/integrations/LeadApiExplorerModal.vue'
import { getEffectiveBackendUrl } from '@/config/app'
import '@/assets/styles/integration-section.css'
import '@/assets/styles/integration-modal.css'

const props = defineProps({
  workspaceId: { type: Number, required: true }
})

const { t } = useI18n()
const toast = useToast()

const keys = ref([])
const loading = ref(false)
const activeTab = ref('cURL')
const tabs = ['cURL', 'JavaScript', 'PHP']
const secretModal = ref(false)
const revealedSecret = ref('')
const createModal = ref(false)
const newKeyName = ref('')
const creating = ref(false)
const explorerOpen = ref(false)

const apiUrl = computed(() => {
  const base = getEffectiveBackendUrl() || ''
  return `${base.replace(/\/$/, '')}/public/leads`
})

const apiCrmBase = computed(() => {
  const base = getEffectiveBackendUrl() || ''
  return `${base.replace(/\/$/, '')}/public/crm-api`
})

const placeholderSecret = 'bl_live_SUA_CHAVE_AQUI'

const crmApiEndpoints = computed(() => {
  const base = apiCrmBase.value
  const secret = placeholderSecret
  const authHeader = `-H "Authorization: Bearer ${secret}"`
  return [
    {
      id: 'createLeadWhatsapp',
      method: 'POST',
      url: `${base}/leads`,
      titleKey: 'integracao.leadApi.ep.createLeadWhatsappTitle',
      descKey: 'integracao.leadApi.ep.createLeadWhatsappDesc',
      curl: `curl -X POST "${base}/leads" \\\n  -H "Content-Type: application/json" \\\n  ${authHeader} \\\n  -d '{"whatsapp":"5541999999999","nome":"Maria Silva"}'`
    },
    {
      id: 'renameLead',
      method: 'PATCH',
      url: `${base}/leads/nome`,
      titleKey: 'integracao.leadApi.ep.renameLeadTitle',
      descKey: 'integracao.leadApi.ep.renameLeadDesc',
      curl: `curl -X PATCH "${base}/leads/nome" \\\n  -H "Content-Type: application/json" \\\n  ${authHeader} \\\n  -d '{"whatsapp":"5541999999999","nome":"Maria S."}'`
    },
    {
      id: 'lookup',
      method: 'GET',
      url: `${base}/leads/lookup?whatsapp=5541999999999`,
      titleKey: 'integracao.leadApi.ep.lookupTitle',
      descKey: 'integracao.leadApi.ep.lookupDesc',
      curl: `curl -X GET "${base}/leads/lookup?whatsapp=5541999999999" \\\n  ${authHeader}`
    },
    {
      id: 'tags',
      method: 'GET',
      url: `${base}/tags`,
      titleKey: 'integracao.leadApi.ep.tagsTitle',
      descKey: 'integracao.leadApi.ep.tagsDesc',
      curl: `curl -X GET "${base}/tags" \\\n  ${authHeader}`
    },
    {
      id: 'users',
      method: 'GET',
      url: `${base}/users`,
      titleKey: 'integracao.leadApi.ep.usersTitle',
      descKey: 'integracao.leadApi.ep.usersDesc',
      curl: `curl -X GET "${base}/users" \\\n  ${authHeader}`
    },
    {
      id: 'agendamentos',
      method: 'GET',
      url: `${base}/agendamentos`,
      titleKey: 'integracao.leadApi.ep.agendamentosTitle',
      descKey: 'integracao.leadApi.ep.agendamentosDesc',
      curl: `curl -X GET "${base}/agendamentos" \\\n  ${authHeader}`
    },
    {
      id: 'addTag',
      method: 'POST',
      url: `${base}/leads/tags`,
      titleKey: 'integracao.leadApi.ep.addTagTitle',
      descKey: 'integracao.leadApi.ep.addTagDesc',
      curl: `curl -X POST "${base}/leads/tags" \\\n  -H "Content-Type: application/json" \\\n  ${authHeader} \\\n  -d '{"whatsapp":"5541999999999","tagId":1}'`
    },
    {
      id: 'removeTag',
      method: 'DELETE',
      url: `${base}/leads/tags?whatsapp=5541999999999&tagId=1`,
      titleKey: 'integracao.leadApi.ep.removeTagTitle',
      descKey: 'integracao.leadApi.ep.removeTagDesc',
      curl: `curl -X DELETE "${base}/leads/tags?whatsapp=5541999999999&tagId=1" \\\n  ${authHeader}`
    },
    {
      id: 'agendamentoForms',
      method: 'GET',
      url: `${base}/agendamento-forms`,
      titleKey: 'integracao.leadApi.ep.agendamentoFormsTitle',
      descKey: 'integracao.leadApi.ep.agendamentoFormsDesc',
      curl: `curl -X GET "${base}/agendamento-forms" \\\n  ${authHeader}`
    },
    {
      id: 'assignUser',
      method: 'POST',
      url: `${base}/leads/assign-user`,
      titleKey: 'integracao.leadApi.ep.assignUserTitle',
      descKey: 'integracao.leadApi.ep.assignUserDesc',
      curl: `curl -X POST "${base}/leads/assign-user" \\\n  -H "Content-Type: application/json" \\\n  ${authHeader} \\\n  -d '{"whatsapp":"5541999999999","usuarioId":1}'`
    }
  ]
})

const codeSample = computed(() => {
  const url = apiUrl.value
  const secret = placeholderSecret
  const body = `{
  "nome": "Maria Silva",
  "email": "maria@empresa.com",
  "telefone": "5541999999999",
  "empresa": "ACME Ltda",
  "cargo": "Diretora",
  "cnpj": "12.345.678/0001-90",
  "origem": "API - ERP",
  "observacoes": "Lead do integrador"
}`
  if (activeTab.value === 'cURL') {
    return `curl -X POST "${url}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${secret}" \\
  -d '${body.replace(/\n/g, '\n  ')}'`
  }
  if (activeTab.value === 'JavaScript') {
    return `const response = await fetch("${url}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${secret}"
  },
  body: JSON.stringify(${body})
});
const data = await response.json();
console.log(data);`
  }
  return `<?php
$ch = curl_init("${url}");
curl_setopt_array($ch, [
  CURLOPT_POST => true,
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_HTTPHEADER => [
    "Content-Type: application/json",
    "Authorization: Bearer ${secret}"
  ],
  CURLOPT_POSTFIELDS => '${body.replace(/\n/g, '')}'
]);
$response = curl_exec($ch);
curl_close($ch);
echo $response;`
})

async function load () {
  loading.value = true
  try {
    const { data } = await api.get(`/workspace/${props.workspaceId}/lead-api-keys`)
    keys.value = Array.isArray(data) ? data : []
  } catch (e) {
    toast.error(e.response?.data?.error || t('integracao.leadApi.loadError'))
  } finally {
    loading.value = false
  }
}

function openCreateKey () {
  newKeyName.value = ''
  createModal.value = true
}

async function createKey () {
  if (!newKeyName.value?.trim()) {
    toast.error(t('integracao.leadApi.nameRequired'))
    return
  }
  creating.value = true
  try {
    const { data } = await api.post(`/workspace/${props.workspaceId}/lead-api-keys`, {
      nome: newKeyName.value.trim()
    })
    createModal.value = false
    revealedSecret.value = data.secret
    secretModal.value = true
    await load()
  } catch (e) {
    toast.error(e.response?.data?.error || t('integracao.leadApi.createError'))
  } finally {
    creating.value = false
  }
}

async function rotateKey (k) {
  if (!confirm(t('integracao.leadApi.rotateConfirm', { name: k.nome }))) return
  try {
    const { data } = await api.post(`/workspace/${props.workspaceId}/lead-api-keys/${k.id}/rotate`)
    revealedSecret.value = data.secret
    secretModal.value = true
    await load()
  } catch (e) {
    toast.error(e.response?.data?.error || t('integracao.leadApi.rotateError'))
  }
}

async function revokeKey (k) {
  if (!confirm(t('integracao.leadApi.revokeConfirm', { name: k.nome }))) return
  try {
    await api.delete(`/workspace/${props.workspaceId}/lead-api-keys/${k.id}`)
    toast.success(t('integracao.leadApi.revokedOk'))
    await load()
  } catch (e) {
    toast.error(e.response?.data?.error || t('integracao.leadApi.revokeError'))
  }
}

function formatDate (d) {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleString()
  } catch {
    return '—'
  }
}

async function copyCode () {
  await copyText(codeSample.value)
}

async function copyText (text) {
  await navigator.clipboard.writeText(text)
  toast.success(t('integracao.leadApi.copied'))
}

async function copySecret () {
  await navigator.clipboard.writeText(revealedSecret.value)
  toast.success(t('integracao.leadApi.copied'))
}

onMounted(load)
</script>
